import { createSelector } from 'reselect'
import {
    JobRequestAttachmentsWithCategoryType,
    FileAttachmentType,
} from '@OpsQuality/types'
import {
    adminJobRequestQuery_jobRequest_parts_linesWithWorkOrderMapping as JobRequestLineType,
    adminJobRequestQuery_jobRequest as JobRequestType,
} from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'
import {
    JOB_REQUEST_STATE,
    HQ_UPLOAD_STATUS,
    ALLOWED_ATTACHMENT_SOURCE_TYPE,
} from '@OpsQuality/constants'

import { AllowedAttachmentSources } from '~/__generated__/globalTypes'
import { AttachmentsType } from '~/components/FileUploadList/types'

import { getAdminJobRequestsQuality_adminJobRequests_items_jobRequestAttachments as JobRequestAttachments } from './JobRequestList/components/__generated__/getAdminJobRequestsQuality'

export const getBasicRevision = (jobRequestLine: any) =>
    jobRequestLine?.partProduct?.partProductRevision?.basicRevision

export const getAngledImage = createSelector(
    getBasicRevision,
    basicRevision => basicRevision?.images?.['angled'],
)

export const getReferencesOfJobRequestAttachments = (
    jobRequestLines: (JobRequestLineType | null)[],
): JobRequestLineType[] => {
    const allJobRequestAttachments = jobRequestLines
        .map(item => item?.jobRequestLineAttachments || [])
        .flat()

    jobRequestLines.forEach(item => {
        const jobRequestAttachments = item
            ? [...item.jobRequestLineAttachments]
            : []

        jobRequestAttachments.forEach(
            (jobRequestAttachment: JobRequestAttachmentsWithCategoryType) => {
                const referentialAttachments = allJobRequestAttachments.filter(
                    woa =>
                        woa.attachment?.id ===
                        jobRequestAttachment.attachment?.id,
                )

                return { ...jobRequestAttachment, referentialAttachments }
            },
        )
        return { ...item, jobRequestAttachments }
    })
    return jobRequestLines as JobRequestLineType[]
}

export const isCompleted = (jobRequest?: { status: string }) => {
    if (!jobRequest) {
        return false
    }

    return (
        jobRequest.status === JOB_REQUEST_STATE.COMPLETED ||
        jobRequest.status === JOB_REQUEST_STATE.EXPIRED ||
        jobRequest.status === JOB_REQUEST_STATE.REJECTED
    )
}

export const transformFileToJobRequestAttachment = (
    fileAttachment: FileAttachmentType,
    attachmentType: string,
    attachmentSource: AllowedAttachmentSources,
    selectedWorkOrderItems: JobRequestLineType[],
) => {
    const allJobRequestAttachments: JobRequestAttachmentsWithCategoryType[] = []

    selectedWorkOrderItems.forEach(item => {
        const fileData = fileAttachment.uploadData?.find(
            attachment =>
                attachment && attachment.workOrderItemName === item.name,
        )

        if (!fileData?.attachment) {
            return null
        }
        allJobRequestAttachments.push({
            __typename: 'WorkOrderAttachment' as const,
            id: fileData?.id,
            attachment: {
                __typename: 'Asset' as const,
                id: fileData?.attachment?.id,
                fileName: fileData?.attachment?.fileName || '',
                extension: fileData?.attachment?.extension,
                downloadUrl: fileData?.attachment?.downloadUrl,
                mimeType: fileData?.attachment?.mimeType,
                uploadUrl: fileData?.attachment?.uploadUrl,
            },
            type: fileData?.type || attachmentType,
            workOrderItemName: fileData?.workOrderItemName || item.name,
            source: fileData?.source || attachmentSource,
            createdAt: fileData.createdAt,
            createdBy: {
                __typename: 'BasicUser' as const,
                id: item.id,
                displayName: '',
            },
            visibleToCustomer: fileData.visibleToCustomer || null,
            referentialAttachments: fileAttachment?.uploadData,
        })
    })

    return allJobRequestAttachments
}

export const hqUploadStatus = (
    jobRequestAttachments: JobRequestAttachments[],
) => {
    if (
        jobRequestAttachments.find(
            attachment =>
                attachment.source === ALLOWED_ATTACHMENT_SOURCE_TYPE.QC,
        )
    ) {
        return HQ_UPLOAD_STATUS.AVAILABLE
    }
    return HQ_UPLOAD_STATUS.NOT_AVAILABLE
}

export const getAllJobRequestAttachments = (jobRequest: JobRequestType) =>
    jobRequest?.jobRequestAttachments || []

export const successContentForMessages = {
    title: 'Your message has been sent',
}

export const hasMpUploadedAttachments = (jobRequest: JobRequestType) => {
    const jobRequestAttachments = getAllJobRequestAttachments(jobRequest)

    return jobRequestAttachments.some(attachment => {
        return (
            (attachment.type === 'fictiv5' ||
                attachment.type === 'additional-docs' ||
                attachment.type === 'photos') &&
            (attachment?.attachment?.mimeType || '').indexOf('image') > -1
        )
    })
}

export const downloadReport = (url: string) => {
    const { body } = document

    if (body) {
        const link = document.createElement('a')

        link.href = url
        link.setAttribute('download', `Inspection-Report.pdf`)
        body.appendChild(link)
        link.click()
        body.removeChild(link)
    }
}

export const getUniqueWorkOrderAttachmentsData = (
    workOrderAttachmentsData: AttachmentsType[],
) => {
    const uniqWorkOrderAttachments: AttachmentsType[] = []

    const attachmentDataMap = new Map()

    workOrderAttachmentsData.forEach((item: AttachmentsType) => {
        if (!attachmentDataMap.has(item?.attachment?.id)) {
            attachmentDataMap.set(item?.attachment?.id, true)
            uniqWorkOrderAttachments.push(item)
        }
    })

    return uniqWorkOrderAttachments
}
